import { ASSETS, ASSETS_BASE_URL } from '@/constants/environment'
import { reportToSensorsData } from '@shared/utils/report'
import { sentryReport, sentryBreadcrumb } from '@shared/utils/sentry-report'

const sameLastSlash = (target: string, refStr: string) => {
  if (refStr.slice(-1) === '/' && target.slice(-1) !== '/') {
    return target + '/'
  }
  if (refStr.slice(-1) !== '/' && target.slice(-1) === '/') {
    return target.slice(0, -1)
  }
  return target
}

export const urlJoin = (...args: string[]) => {
  const join = (a: string, b: string) => {
    if (!a || !b) {
      return a || b
    }
    a = a.replace(/\/*$/, '')
    b = b.replace(/^\/*/, '')
    return `${a}/${b}`
  }
  return args.reduce((a, n) => join(a, n))
}

// 取 ASSETS_BASE_URL、ASSETS 的 key
const PICK_DOMAIN = ['CRM', 'MAIL_PRO', 'SALES', 'DX', 'COMMON_QIANKUN', 'MK', 'LEADS'].filter(
  (n) => n in ASSETS_BASE_URL,
)

// 仅与 shell 一起打包发版的项目才允许 webpack-assets.json 拼接 CommitID，使用 CDN
export const CAN_TO_CND_FOR_WEBPACK_JSON = ['CRM', 'MAIL_PRO', 'SALES', 'DX', 'COMMON_QIANKUN', 'MK'].map(
  (p) => ASSETS_BASE_URL[p],
)

// CDN
export const REPLACE_CDN_DOMAIN = PICK_DOMAIN.map((p) => ASSETS_BASE_URL[p])

// 主域
export const REPLACE_MAIN_DOMAIN = PICK_DOMAIN.map((p) => ASSETS[p])
  .map((url) => urlJoin(url.startsWith('//') ? '' : location.origin, url))
  .map((url, i) => sameLastSlash(url, REPLACE_CDN_DOMAIN[i]))

export const toMainDomain = (url: string) => {
  const matchIndex = REPLACE_CDN_DOMAIN.findIndex((n) => url.includes(n))
  // 兼容原本就在主域的情况（测试环境）
  if (~matchIndex && !url.includes(REPLACE_MAIN_DOMAIN[matchIndex])) {
    return url.replace(REPLACE_CDN_DOMAIN[matchIndex], REPLACE_MAIN_DOMAIN[matchIndex])
  }
  return url
}

type Params = Parameters<typeof window['fetch']>
export const createRepeatFetch = <T extends typeof window['fetch']>(
  fetch: T,
  options: {
    max: number
    // curCount，当前是第几次 fetch，curCount >= 2
    // 可转换 url ，返回 fetch 的新参数
    onRepeat?: (args: Params, curCount: number) => Params
    onFinally?: (res: {
      isSuccess: boolean
      count: number
      value: any
      finallyUrl: string
      messages?: string[]
    }) => void
  },
) => {
  const { max, onRepeat = (args) => args, onFinally } = options
  let count = 0
  const messages: string[] = []
  const realFetch = (...args: Params): Promise<Response> => {
    count++
    return fetch(...args)
      .then((res) => {
        onFinally?.({ isSuccess: true, count, value: res, finallyUrl: args[0] as string, messages })
        return res
      })
      .catch((err) => {
        messages.push(err?.message || `Catch ${JSON.stringify(err)}`)
        if (count < max) {
          return realFetch(...onRepeat(args, count + 1))
        }
        onFinally?.({ isSuccess: false, count, value: err, finallyUrl: args[0] as string, messages })
        return Promise.reject(err)
      })
  }
  return realFetch as T
}

const getFileSuffix = (url: string) => {
  const match = url.match(/\.(\w+)([\?#].*)?$/)
  return (match?.[1] || '').toLocaleLowerCase()
}
const CHECK_SUFFIX = ['js', 'css', 'json']

export const isWhiteListFileType = (url: string) => CHECK_SUFFIX.includes(getFileSuffix(url))

export const sourceFetch = (...args: Params) => {
  const fetch: typeof window.fetch = window.proxy?.fetch || window.fetch
  return fetch(...args).then((res) => {
    // 状态码校验
    if (res.status >= 400) {
      throw new Error(`${args[0]} load failed with status ${res.status}`)
    }
    // 资源内容校验
    const suffix = getFileSuffix(res.url)
    if (CHECK_SUFFIX.includes(suffix)) {
      return res.text().then((text) => {
        const part = text.trimStart().slice(0, 100)
        if (/^(<html)|(<head)|(<!DOCTYPE)/.test(part)) {
          throw new Error(`${args[0]} load failed with response text ${part}`)
        }
        if (suffix === 'json') {
          try {
            text = JSON.parse(text)
          } catch (error) {
            throw new Error(`${args[0]} load failed with response json.parse ${error}`)
          }
        }
        return new Proxy(res, {
          get(target, key) {
            if (key === 'text' || key === 'json') {
              return () => Promise.resolve(text)
            }
            return Reflect.get(target, key)
          },
        })
      })
    }
    return res
  })
}

export const getBuildInfo = () => {
  const content = (document.querySelector('meta[name="build-info"]') as any)?.content
  if (!content) {
    return null
  }
  return JSON.parse(content) as {
    projectName: string
    shortCommit: string
    longCommit: string
    branch: string
    buildTime: string
  }
}

export const reportRetry = (key: string, data: any) => {
  reportToSensorsData(key.replace(/-/g, '_'), data)
  if (!key.includes('repeat-success')) {
    sentryReport(key, data, 'report-retry')
  }
}

export const addRetrySentryBreadcrumb = (
  type: string,
  data: {
    curCount: number
    url: string
    target?: string
  },
) => {
  if (data.target && data.url === data.target) {
    delete data.target
  }
  sentryBreadcrumb({
    category: `retry-${type}`,
    message: '资源异常，发起重试',
    data,
  })
}
